import { c as _c } from "react/compiler-runtime";
// Third-Party
import * as React from 'react';
import { FormikValues, FormikHelpers, ErrorMessage } from 'formik';
import { twMerge } from 'tailwind-merge';

// UI
import PhoneInput, { Country } from 'react-phone-number-input';
import { getFieldInputStylesAsClassName } from '../field/field-styles';
import { getFieldContainerStylesAsClassName } from '../field/field-container/field-container-styles';
const DEFAULT_COUNTRIES: Country[] = ['US', 'CA', 'MX'];
export type PhoneNumberInputProps = {
  title: string;
  field: FormikValues;
  form: FormikHelpers<unknown>;
  disabled?: boolean;
  /**
   * Restrict the country picker to this list. Pass `null` to allow all
   * countries supported by `react-phone-number-input`. Defaults to
   * US/CA/MX for backwards compatibility with existing callers.
   */
  countries?: Country[] | null;
  defaultCountry?: Country;
};
const StyledPhoneInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const $ = _c(15);
  const newProps = {
    ...props,
    ...{
      style: {},
      className: "input"
    }
  };
  const {
    className: classNameProp,
    ...newPropsWithoutClassName
  } = newProps;
  const [isFocused, setIsFocused] = React.useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = _event => {
      setIsFocused(false);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const onBlurInput = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = _event_0 => {
      setIsFocused(true);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onFocusInput = t1;
  let t2;
  if ($[2] !== isFocused || $[3] !== props.disabled) {
    t2 = twMerge(getFieldContainerStylesAsClassName(props.disabled, isFocused, false, false), "relative w-full");
    $[2] = isFocused;
    $[3] = props.disabled;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const outerTwClassName = t2;
  const innerTwClassName = twMerge(getFieldInputStylesAsClassName(false, true, props.disabled, false), classNameProp);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <label className="z-50 px-1  absolute left-2 top-18px text-gray-dark leading-4 -mt-3.5 text-xs">Phone Number</label>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== innerTwClassName || $[7] !== newPropsWithoutClassName || $[8] !== ref) {
    t4 = <input className={innerTwClassName} ref={ref} type="text" {...newPropsWithoutClassName} />;
    $[6] = innerTwClassName;
    $[7] = newPropsWithoutClassName;
    $[8] = ref;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onBlurInput || $[11] !== onFocusInput || $[12] !== outerTwClassName || $[13] !== t4) {
    t5 = <div className={outerTwClassName} onBlur={onBlurInput} onFocus={onFocusInput}>{t3}{t4}</div>;
    $[10] = onBlurInput;
    $[11] = onFocusInput;
    $[12] = outerTwClassName;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
});
StyledPhoneInput.displayName = 'StyledPhoneInput';
export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = t0 => {
  const $ = _c(19);
  const {
    field,
    form,
    disabled,
    countries: t1,
    defaultCountry: t2
  } = t0;
  const countries = t1 === undefined ? DEFAULT_COUNTRIES : t1;
  const defaultCountry = t2 === undefined ? "US" : t2;
  const [isFocused, setIsFocused] = React.useState(false);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = _event => {
      setIsFocused(false);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const onBlurInput = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = _event_0 => {
      setIsFocused(true);
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const onFocusInput = t4;
  const t5 = countries ?? undefined;
  let t6;
  if ($[2] !== field.name || $[3] !== form) {
    t6 = value => {
      form.setFieldTouched(field.name);
      form.setFieldValue(field.name, value);
    };
    $[2] = field.name;
    $[3] = form;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== defaultCountry || $[6] !== disabled || $[7] !== field.value || $[8] !== t5 || $[9] !== t6) {
    t7 = <PhoneInput inputComponent={StyledPhoneInput} international={true} countries={t5} defaultCountry={defaultCountry} value={field.value} disabled={disabled} onBlur={onBlurInput} onFocus={onFocusInput} onChange={t6} data-testid="@phone/phone-number-input" />;
    $[5] = defaultCountry;
    $[6] = disabled;
    $[7] = field.value;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== field.name || $[12] !== isFocused) {
    t8 = !isFocused && <ErrorMessage name={field.name} render={_temp} />;
    $[11] = field.name;
    $[12] = isFocused;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <div className="pl-16 mt-1">{t8}</div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t7 || $[17] !== t9) {
    t10 = <div>{t7}{t9}</div>;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
function _temp(msg) {
  return <p data-testid="phoneNumber-error" className="font-semibold text-s text-error-main">{msg}</p>;
}